import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../components/CatalogListHeader/CatalogListHeader';
import EmptyListResult from '../../components/EmptyListResult/EmptyListResult';
import {
  ActionIcon,
  Badge,
  Button,
  Grid,
  MultiSelect,
  Select,
  TextInput,
  Tooltip
} from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IconBrandWhatsapp,
  IconCircleDashedCheck,
  IconCurrencyDollar,
  IconEdit,
  IconFileSpreadsheet,
  IconPhoto,
  IconPrinter,
  IconReceiptDollar,
  IconShirt,
  IconTimeline
} from '@tabler/icons-react';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import CatalogCard from '../../components/CatalogCard/CatalogCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import useDownloadExcel from '../../hooks/useDownloadExcel/useDownloadExcel';
import { formatMoneyString, getCustomerFullName, getWhatsappURLString } from '../../utils/strings';
import { useGetOrders } from '../../hooks/orders/queries';
import { IOrder } from '../../interfaces/Order';
import OrderStatuses, { OrderStatusesType } from '../../enum/OrderStatuses';
import { IconCFDI } from '../../assets/icons/IconCFDI';
import { useDisclosure } from '@mantine/hooks';
import OrderTimeLineModal from '../../components/OrderModals/OrderTimeLineModal';
import OrderPaymentLinesModal from '../../components/OrderModals/OrderPaymentLinesModal';
import { showNotification } from '../../utils/Notification/Notification';
import { exportOrdersListV2 } from '../../services/order.service';
import { tryPrintTicket } from '../../utils/ticketHelper';
import { useSelector } from 'react-redux';
import OrderCreateCFDIModal from '../../components/OrderModals/OrderCreateCFDIModal';
import { useGetCFDIPreviewV2 } from '../../hooks/cfdi/queries';
import { useCreateCFDI } from '../../hooks/cfdi/mutations';
import OrderProductsModal from '../../components/OrderModals/OrderProductsModal';
import OrderEvidencesCarouselModal from '../../components/OrderModals/OrderEvidencesCarouselModal';
import OrdersCardMoreActionsMenu from '../../components/OrdersCardMoreActionsMenu';
import PrintTicketDrawer from '../../components/PrintTicketDrawer';
import {
  buildOrderFolio,
  getOrderCardColorsByAmountDueAndStatus,
  getOrderInvoiceSearchId,
  isOrderPartiallyCollected
} from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import { IStore } from '../../interfaces/Store';
import { useGetStoreById } from '../../hooks/stores/queries';
import CardLoaderSkeletonGridRows from '../../components/Skeletons/CardLoaderSkeletonGridRows';
import { MercadoPagoPaymentSheet } from '../../components/MercadoPagoPaymentSheet';
import { getPiecesBreakdown, formatPiecesBreakdown } from '../../utils/helpers/getAllPieces';
import { useOrderPaymentLinesModal } from '../../hooks/useOrderPaymentLinesModal';
import useCanUseCFDIInvoicing from '../../hooks/useCanUseCFDIInvoicing';
import { MercadoPagoEnum } from '../../enum/PaymentConfig';
import { autoOpenCashDrawerForPayments } from '../../utils/cashDrawer';
import useCanEditOrders from '../../hooks/useCanEditOrders';
import useCanValidateStoreReception from '../../hooks/useCanValidateStoreReception';
import {
  STORE_RECEPTION_VALIDATION_QUERY_MODE,
  canShowValidateStoreReceptionAction,
  getOrderStoreId
} from '../../utils/storeReceptionValidation';
import { hasPermission } from '../../utils/permission-utils';

const List = () => {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'Notifications', 'OrdersPage', 'NewOrder']);
  const operationalReturnLabel = t('NewOrder:operationalReturns.badge');
  const { company, user } = useSelector((state: any) => state.user);
  const canEditCustomerFiscalData = hasPermission(user, 'customers', 'canEdit');
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const { canViewCFDI, canMutateCFDI } = useCanUseCFDIInvoicing(searchParamStoreId);
  const canEditOrders = useCanEditOrders();
  const canValidateStoreReception = useCanValidateStoreReception();
  const searchTermInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [orderStatusFilter, setOrderStatusFilter] = useState<string | undefined>(undefined);
  const [orderStatusFilterArray, setOrderStatusFilterArray] = useState<string[]>([]);
  const [orderSelectedYear, setOrderSelectedYear] = useState<string>(moment().format('YYYY'));

  const setSearchParamsDebounced = debounce((val: string) => {
    setSearchTerm(val || undefined);
  }, 500);
  const queryClient = useQueryClient();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [openedOrderProductsModal, orderProductsModalhandlers] = useDisclosure(false);
  const [openedOrderTimelineModal, orderTimelineModalHandlers] = useDisclosure(false);
  const [openedOrderCreateCFDIModal, orderCreateCFDIlineModalHandlers] = useDisclosure(false);
  const [openedOrderEvidenceModal, evidenceModalhandlers] = useDisclosure(false);
  const [openedMercadoPagoModal, { open: openMercadoPagoModal, close: closeMercadoPagoModal }] =
    useDisclosure(false);
  const [openedPrintTicketDrawer, { open: openPrintTicketDrawer, close: closePrintTicketDrawer }] =
    useDisclosure(false);
  const { data: store } = useGetStoreById(!!searchParamStoreId, searchParamStoreId);
  const storeReceptionValidationEnabled = store?.storeReceptionValidationConfig?.enabled === true;
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);
  const [printTicketOrder, setPrintTicketOrder] = useState<IOrder | null>(null);
  const paymentLinesModal = useOrderPaymentLinesModal({ store });
  const {
    isLoading: isGetCFDILoading,
    data: CFDIInfo,
    error: cfdiPreviewError
  } = useGetCFDIPreviewV2(
    {
      ordersID: selectedOrder?._id ? [selectedOrder?._id] : undefined,
      customer: selectedOrder?.customer?._id || ''
    },
    !!selectedOrder?._id && openedOrderCreateCFDIModal
  );

  const { mutateAsync: createCFDIAsync, isPending: isCreationCFDIPending } = useCreateCFDI();
  const orderCreateCFDIInfo = useMemo(
    () => ({
      ...CFDIInfo,
      orderID: selectedOrder?._id,
      facturapiInvoiceID: selectedOrder?.facturapiInvoiceID
    }),
    [CFDIInfo, selectedOrder?._id, selectedOrder?.facturapiInvoiceID]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch: refetchList,
    isLoading
  } = useGetOrders(!!searchParamStoreId, {
    q: searchTerm,
    status: orderStatusFilter,
    storeId: searchParamStoreId,
    fromDate: moment()
      .set('year', +orderSelectedYear)
      .startOf('year')
      .toISOString(),
    toDate: moment()
      .set('year', +orderSelectedYear)
      .endOf('year')
      .toISOString(),
    limit: 9,
    fetchSection: 'true'
  });

  const orders = useMemo(() => {
    return data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);

  const showOrderProductsModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderProductsModalhandlers.open();
  };

  const showOrderTimeLineModal = (order: IOrder) => {
    setSelectedOrder(order);
    orderTimelineModalHandlers.open();
  };

  const showPaymentLinesModal = async (order: IOrder) => {
    setSelectedOrder(order);
    await paymentLinesModal.open(order);
  };

  const showCreateCFDIModal = (order: IOrder) => {
    if (!canMutateCFDI) return;
    setSelectedOrder(order);
    orderCreateCFDIlineModalHandlers.open();
  };

  const onAddPaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {}
  };

  const onDeletePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onUpdatePaymentLine = async () => {
    try {
      await refetchList();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const clearSearchTerm = () => {
    if (searchTermInputRef.current) {
      searchTermInputRef.current.value = ''; // Reset the input field value via ref
      setSearchParamsDebounced(undefined); // Reset the state to trigger re-render
      setOrderStatusFilter(undefined); // Optionally reset other filters if needed
      setOrderStatusFilterArray([]); // Optionally reset other filters if needed
      setOrderSelectedYear(moment().format('YYYY'));
    }
  };

  const onPrintTicket = async (
    order: IOrder,
    ticketStructureConfig: 'ticketForLaundryStructure' | 'ticketStructure' = 'ticketStructure'
  ) => {
    try {
      tryPrintTicket(
        order,
        order.createdIn,
        t,
        ticketStructureConfig,
        company.logoUrl,
        false,
        company?.companyTaxInfo
      );
    } catch (e) {}
  };

  const handleOpenPrintTicketDrawer = (order: IOrder) => {
    setPrintTicketOrder(order);
    openPrintTicketDrawer();
  };

  const handleClosePrintTicketDrawer = () => {
    closePrintTicketDrawer();
    setPrintTicketOrder(null);
  };

  const showOrderEvidenceModal = (order: IOrder) => {
    setSelectedOrder(order);
    evidenceModalhandlers.open();
  };

  const handleEvidenceDeleted = (evidenceId: string) => {
    setSelectedOrder((prev) =>
      prev
        ? {
            ...prev,
            evidence: prev.evidence?.filter((evidence) => evidence._id !== evidenceId) ?? []
          }
        : prev
    );
  };

  const showMercadoPagoModal = (order: IOrder) => {
    setSelectedOrder(order);
    openMercadoPagoModal();
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['orders']
      });
    };
  }, []);

  useEffect(() => {
    if (data && selectedOrder) {
      const updatedOrder = orders.find((order) => order._id === selectedOrder._id);
      if (updatedOrder) {
        setSelectedOrder(updatedOrder);
        paymentLinesModal.syncOrder(updatedOrder);
      }
    }
  }, [data, selectedOrder?._id]);

  useEffect(() => {
    setOrderStatusFilter(orderStatusFilterArray.toString() || undefined);
  }, [orderStatusFilterArray]);

  return (
    <CatalogContainer>
      <MercadoPagoPaymentSheet
        opened={openedMercadoPagoModal}
        onClose={() => {
          closeMercadoPagoModal();
          setSelectedOrder(null);
        }}
        onApproved={() => {
          try {
            refetchList();
          } catch (e) {}
          const amountDue = Math.max(0, (selectedOrder?.total || 0) - (selectedOrder?.amountPaid || 0));
          void autoOpenCashDrawerForPayments({
            cashDrawerConfig: store?.cashDrawerConfig,
            payments: [{ paymentMethod: MercadoPagoEnum.MERCADOPAGO, amountPaid: amountDue }],
            source: 'payment',
          }).then((cashDrawerResult) => {
            if (cashDrawerResult.attempted && !cashDrawerResult.success) {
              showNotification({
                type: 'Error',
                title: t('Notifications:cashDrawerOpenFailedTitle'),
                message: cashDrawerResult.errorMessage || t('Notifications:cashDrawerOpenFailedBody'),
              });
            }
          });
        }}
        attemptId={null}
        order={
          selectedOrder
            ? {
                ...selectedOrder,
                sequence: selectedOrder?.sequence || '',
                _id: selectedOrder?._id || '',
                cashierBoxId: '',
                total: selectedOrder?.total || 0,
                amountPaid: selectedOrder?.amountPaid || 0,
                currency: selectedOrder?.currency || 'MXN',
                paymentLines: selectedOrder?.paymentLines || [],
                createdIn: {
                  _id: selectedOrder?.createdIn?._id?.toString() || selectedOrder?.createdIn || '',
                  currencySymbol: selectedOrder?.createdIn?.currencySymbol
                }
              }
            : null
        }
        print_on_terminal={store?.mpSettings?.print_on_terminal}
        storeId={searchParamStoreId}
        autoCloseOnApproved={false}
        onPrintTicket={(order) =>
          onPrintTicket({
            ...selectedOrder,
            ...order,
            createdIn: selectedOrder?.createdIn
          })
        }
      />
      <PrintTicketDrawer
        opened={openedPrintTicketDrawer}
        onClose={handleClosePrintTicketDrawer}
        order={printTicketOrder}
      />
      <OrderEvidencesCarouselModal
        title={`${t('OrdersPage:evidenceModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          evidenceModalhandlers.close();
        }}
        opened={openedOrderEvidenceModal}
        order={selectedOrder}
        onEvidenceDeleted={handleEvidenceDeleted}
      />
      <OrderPaymentLinesModal
        onAddPaymentLine={onAddPaymentLine}
        title={paymentLinesModal.title}
        close={paymentLinesModal.close}
        opened={paymentLinesModal.opened}
        order={paymentLinesModal.order}
        onDeletePaymentLine={onDeletePaymentLine}
        onUpdatePaymentLine={onUpdatePaymentLine}
        pinUser={paymentLinesModal.pinUser}
      />
      <OrderProductsModal
        {...orderProductsModalhandlers}
        title={`${t('OrdersPage:productsModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          orderProductsModalhandlers.close();
        }}
        opened={openedOrderProductsModal}
        order={selectedOrder}
      />
      <OrderTimeLineModal
        {...orderTimelineModalHandlers}
        title={`${t('OrdersPage:timelineModal.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          orderTimelineModalHandlers.close();
        }}
        opened={openedOrderTimelineModal}
        order={selectedOrder}
      />
      <OrderCreateCFDIModal
        {...orderCreateCFDIlineModalHandlers}
        title={`${t('OrdersPage:createCFDI.title')}: ${buildOrderFolio(
          selectedOrder?.folioPrefix,
          selectedOrder?.sequence
        )}`}
        close={() => {
          orderCreateCFDIlineModalHandlers.close();
        }}
        opened={openedOrderCreateCFDIModal}
        isLoading={isGetCFDILoading || isCreationCFDIPending}
        CFDIInfo={orderCreateCFDIInfo}
        previewError={cfdiPreviewError}
        enableCustomerFiscalDataEditing
        canEditCustomerFiscalData={canEditCustomerFiscalData}
        onCreateCFDI={async (values) => {
          const data = {
            ordersID: values?.orderID ? [values?.orderID] : [],
            CFDIInfo: {
              use: values?.use,
              payment_method: values?.payment_method,
              payment_form: values?.payment_form,
              sendEmailInvoice: values?.sendEmailInvoice,
              date: new Date(values.date).toISOString(),
              customerFiscalProfile: values?.customerFiscalProfile,
              env: undefined, //TODO: ARE WE STILL USNG THIS HERE? MAYBE BACKEND TAKES DECISION
              global: undefined // ONLY NEEDED FOR GLOBAL CFDI
            },
            // CUSTOMER IS NEEDED FOR A INDIVIDUAL CFDI OR CUSTOMER SPECIFIC, IF NOT SENT IT MIGHT BE A GLOBAL CFDI
            customer:
              typeof selectedOrder?.customer === 'string'
                ? selectedOrder?.customer
                : selectedOrder?.customer?._id || ''
          };
          await createCFDIAsync(data);
          orderCreateCFDIlineModalHandlers.close();
          showNotification({ type: 'Default', title: t('Notifications:success') });
        }}
      />
      <CatalogListHeader
        title={t('OrdersPage:title')}
        createButtonText={t('OrdersPage:create')}
        showCreateButton={false}
      />
      {/* <LoaderOverlay visible={isLoading} /> */}
      <Grid>
        <Grid.Col span={12} my={10}>
          <Grid align="center" justify="center">
            <Grid.Col span={{ xs: 12, md: 4 }}>
              <TextInput
                ref={searchTermInputRef}
                onChange={(event) => setSearchParamsDebounced(event.currentTarget.value)}
                placeholder={`${t('FieldName:order')}, ${t('FieldName:customer')}, ${t(
                  'FieldName:phone'
                )}`}
                label={`${t('FieldName:order')}`}
              />
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 3 }}>
              <MultiSelect
                label={t('FieldName:status')}
                placeholder={t('FieldName:status')}
                data={Object.keys(OrderStatuses).reduce((prev: any, statusKey: string) => {
                  if (OrderStatuses[statusKey as OrderStatusesType] !== 'created') {
                    prev.push({
                      value: OrderStatuses[statusKey as OrderStatusesType],
                      label: t(
                        `FieldName:orderStatuses.${OrderStatuses[statusKey as OrderStatusesType]}`
                      )
                    });
                  }
                  return prev;
                }, [])}
                value={orderStatusFilterArray || null}
                onChange={(val) => {
                  setOrderStatusFilterArray(val);
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 2 }}>
              <Select
                label={t('FieldName:year')}
                clearable={false}
                allowDeselect={false}
                placeholder={t('FieldName:year')}
                data={[-4, -3, -2, -1, 0].map((offset) => {
                  let dateOffset = moment().add(offset, 'years');
                  let year = dateOffset.format('YYYY');
                  return {
                    label: year,
                    value: year
                  };
                })}
                value={orderSelectedYear}
                onChange={(val) => {
                  setOrderSelectedYear(val || '');
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
              <Button fullWidth type="button" color="gray" onClick={clearSearchTerm}>
                {t('CommonButtons:clear').toUpperCase()}
              </Button>
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 1 }} style={{ alignSelf: 'flex-end' }}>
              <Tooltip label={t('CommonButtons:exportList')}>
                <ActionIcon
                  disabled={loadingExport}
                  onClick={() => {
                    downloadExcelV2(async () => {
                      try {
                        return await exportOrdersListV2({
                          q: searchTerm,
                          status: orderStatusFilter,
                          storeId: searchParamStoreId,
                          fromDate: moment()
                            .set('year', +orderSelectedYear)
                            .startOf('year')
                            .toISOString(),
                          toDate: moment()
                            .set('year', +orderSelectedYear)
                            .endOf('year')
                            .toISOString()
                        });
                      } catch (e) {
                        throw e;
                      }
                    }, `Ordenes_${searchParamStoreId}`);
                  }}
                  variant="filled"
                  color="#1D6F42"
                  aria-label="Descargar_Listado_CSV_Ordenes"
                >
                  <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
      {/* First page loader */}
      {isLoading && <CardLoaderSkeletonGridRows cardsQty={3} cardContentRows={8} />}
      {orders && orders.length === 0 && <EmptyListResult />}
      <InfiniteScroll
        dataLength={orders?.length || 0}
        next={fetchNextPage}
        hasMore={hasNextPage}
        style={{ overflow: 'hidden', position: 'relative' }}
        loader={<CardLoaderSkeletonGridRows cardsQty={3} cardContentRows={8} />}
        // endMessage={<Empty description={false} />}
      >
        <>
          <Grid gutter="md" pos={'relative'}>
            {orders?.map((order) => {
              const amountDue = getOrderAmountDue(order);
              const paymentLinesTotal = (order.total || 0) - amountDue;
              const invoiceSearchId = getOrderInvoiceSearchId(order);
              const orderStoreId = getOrderStoreId(order, searchParamStoreId);
              const customColors = getOrderCardColorsByAmountDueAndStatus({
                amountDue,
                highlightOrderCardsByPaymentPending: (order.createdIn as Partial<IStore>)
                  ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                status: order.status
              });
              return (
                <Grid.Col key={order._id} span={{ xs: 12, sm: 6, lg: 4 }}>
                  <CatalogCard
                    fields={[
                      {
                        label: t('FieldName:name'),
                        value: getCustomerFullName(order.customer.name, order.customer.lastName)
                      },
                      {
                        label: t('FieldName:total'),
                        value: formatMoneyString((order.total || 0).toFixed(2))
                      },
                      {
                        label: t('FieldName:ordersPaidTotal'),
                        value: formatMoneyString(paymentLinesTotal.toFixed(2))
                      },
                      {
                        label: t('FieldName:status'),
                        value: (
                          <>
                            {t(
                              `FieldName:orderStatuses.${OrderStatuses[order.status.toUpperCase()]}`
                            )}
                            {isOrderPartiallyCollected(order) && (
                              <Badge
                                component="span"
                                color="orange"
                                size="xs"
                                variant="light"
                                ml={4}
                              >
                                {t('FieldName:partialDeliveryBadge')}
                              </Badge>
                            )}
                          </>
                        )
                      },
                      {
                        label: t('FieldName:pieces'),
                        value: formatPiecesBreakdown(
                          getPiecesBreakdown(order.products, order.buyAndGetProducts),
                          {
                            measurementUnits: (order.createdIn as IStore)?.measurementUnits
                          }
                        )
                      },
                      {
                        label: t('FieldName:createdDate'),
                        value: moment(order.createdDate).format('DD/MM/YYYY HH:mm') || ''
                      }
                    ]}
                    cardActionButtons={[
                      {
                        action: () => showOrderTimeLineModal(order),
                        disabled: false,
                        Icon: <IconTimeline />,
                        tooltip: t('OrdersPage:timelineModal.tooltip')
                      },
                      {
                        action: () => showPaymentLinesModal(order),
                        disabled: false,
                        Icon: <IconCurrencyDollar />,
                        tooltip: t('OrdersPage:paymentLinesModal.tooltip'),
                        testId: `order-action-payment-lines-${order._id}`
                      },
                      {
                        action: () => showOrderProductsModal(order),
                        disabled: false,
                        Icon: <IconShirt />,
                        tooltip: t('OrdersPage:productsModal.tooltip')
                      },
                      {
                        action: () => showCreateCFDIModal(order),
                        hidden: !canMutateCFDI,
                        disabled: false,
                        Icon: <IconCFDI size={25} />,
                        tooltip: t('OrdersPage:createCFDI.tooltip')
                      },
                      {
                        action: () =>
                          history.push(
                            `/orders/${order._id}?storeId=${orderStoreId}&mode=${STORE_RECEPTION_VALIDATION_QUERY_MODE}`,
                            {
                              storeID: orderStoreId
                            }
                          ),
                        hidden: !canShowValidateStoreReceptionAction({
                          order,
                          canValidateStoreReception,
                          storeReceptionValidationEnabled
                        }),
                        disabled: false,
                        Icon: <IconCircleDashedCheck />,
                        tooltip: t('OrdersPage:validateStoreReception')
                      },
                      {
                        action: () =>
                          history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
                            storeID: searchParamStoreId
                          }),
                        disabled: !canEditOrders,
                        Icon: <IconEdit />,
                        tooltip: t('CommonButtons:edit')
                      }
                    ]}
                    cardTitle={buildOrderFolio(order?.folioPrefix, order?.sequence) || ''}
                    cardTestId={`order-card-${order._id}`}
                    cardTitleExtra={
                      <>
                        {order.orderType === 'return' ? (
                          <Tooltip label={operationalReturnLabel} withinPortal={false}>
                            <Badge
                              color="blue"
                              mr={4}
                              size="xs"
                              style={{ flexShrink: 0 }}
                              variant="filled"
                            >
                              {operationalReturnLabel}
                            </Badge>
                          </Tooltip>
                        ) : null}
                        {canViewCFDI && invoiceSearchId && (
                          <Tooltip
                            withinPortal={false}
                            label={t('OrdersPage:billingStatus.viewInvoiceTooltip')}
                          >
                            <Badge
                              aria-label={t('OrdersPage:billingStatus.invoiced')}
                              color="blue"
                              component="button"
                              mr={4}
                              onClick={(event) => {
                                event.stopPropagation();
                                history.push(
                                  `/CFDI?storeId=${searchParamStoreId}&q=${encodeURIComponent(
                                    invoiceSearchId
                                  )}`
                                );
                              }}
                              size="xs"
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              type="button"
                              variant="filled"
                            >
                              {t('OrdersPage:billingStatus.invoiced')}
                            </Badge>
                          </Tooltip>
                        )}
                        {(order?.evidence?.length ?? 0) > 0 && (
                          <Tooltip
                            withinPortal={false}
                            label={t('OrdersPage:evidenceModal.viewTooltip')}
                          >
                            <ActionIcon
                              color={customColors.headerTextColor}
                              onClick={() => showOrderEvidenceModal(order)}
                              variant="transparent"
                              aria-label={t('OrdersPage:evidenceModal.viewTooltip')}
                            >
                              <IconPhoto />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Imprimir Ticket">
                          <ActionIcon
                            color={customColors.headerTextColor}
                            onClick={() => handleOpenPrintTicketDrawer(order)}
                            variant="transparent"
                            aria-label="Imprimir Ticket"
                          >
                            <IconPrinter />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          label={
                            order.customer?.phone
                              ? t('CommonButtons:sendWhatsappMessage')
                              : t('CommonButtons:noCustomerPhone')
                          }
                        >
                          <ActionIcon
                            color={customColors.headerTextColor || 'green'}
                            disabled={!order.customer.phone}
                            onClick={() => {
                              window.open(
                                getWhatsappURLString(
                                  order.customer?.phone,
                                  order.customer?.phoneCountryCode
                                ),
                                '_blank'
                              );
                            }}
                            variant="transparent"
                            aria-label={
                              order.customer?.phone
                                ? t('CommonButtons:sendWhatsappMessage')
                                : t('CommonButtons:noCustomerPhone')
                            }
                          >
                            <IconBrandWhatsapp />
                          </ActionIcon>
                        </Tooltip>
                        <OrdersCardMoreActionsMenu
                          order={order}
                          customColors={customColors}
                          showMercadoPagoModal={showMercadoPagoModal}
                        />
                      </>
                    }
                    TitleIcon={IconReceiptDollar}
                    customColors={customColors}
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </>
      </InfiniteScroll>
    </CatalogContainer>
  );
};

export default List;
